import { Fragment } from "react";
import { PivotResult } from "./types";
import { num, usd } from "./helpers";

interface Props {
  pivot: PivotResult;
}

export const PivotTable = ({ pivot }: Props) => {
  if (pivot.rows.length === 0) {
    return <div className="text-center py-12 text-gray-500">No data for this view.</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="sticky left-0 bg-gray-50 px-4 py-2 text-left text-[11px] uppercase tracking-wide text-gray-500 z-10">
                {pivot.axisLabel}
              </th>
              {pivot.itemNames.map((name) => (
                <th key={name} colSpan={2} className="px-3 py-2 text-center text-[11px] uppercase tracking-wide text-gray-500 border-l border-gray-200">
                  {name}
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="sticky left-0 bg-gray-50 z-10" />
              {pivot.itemNames.map((name) => (
                <Fragment key={name}>
                  <th className="px-3 py-1 text-right text-[10px] text-gray-400 font-normal border-l border-gray-100">Units</th>
                  <th className="px-3 py-1 text-right text-[10px] text-gray-400 font-normal">Gross</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pivot.rows.map((row) => (
              <tr key={row.key} className="hover:bg-gray-50">
                <td className="sticky left-0 bg-white px-4 py-2 font-medium text-gray-800 whitespace-nowrap z-10">{row.label}</td>
                {pivot.itemNames.map((name) => {
                  const cell = row.cells[name];
                  const empty = !cell || (cell.units === 0 && cell.grossCents === 0);
                  return (
                    <Fragment key={name}>
                      <td className={`px-3 py-2 text-right tabular-nums border-l border-gray-100 ${empty ? "text-gray-300" : "text-gray-800"}`}>
                        {cell ? num(cell.units) : "—"}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${empty ? "text-gray-300" : "text-gray-800"}`}>
                        {cell ? usd(cell.grossCents) : "—"}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
            <tr>
              <td className="sticky left-0 bg-gray-100 px-4 py-2 font-semibold text-gray-900 z-10">Total</td>
              {pivot.itemNames.map((name) => {
                const t = pivot.itemTotals[name];
                return (
                  <Fragment key={name}>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold border-l border-gray-200">
                      {num(t.units)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {usd(t.grossCents)}
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
